// ---------------------------------------------------------------------------
// Roles & permissions model for the "Mantenimiento a Vehículos" mini-app.
//
// The epic defines four maintenance personas. They are derived from the system
// `userRole` (localStorage) so the rest of the sub-tasks can gate UI/actions
// against a single capability matrix instead of scattering role string checks:
//
//   - fleet_admin       → administrador de flota (acceso total)
//   - workshop_internal → mecánico / taller propio
//   - workshop_external → mecánico / taller externo
//   - service_buyer     → comprador del servicio (solo lectura + reportes)
//
// Until dedicated maintenance roles exist in the backend, the mapping is
// intentionally permissive: any unmapped role falls back to `fleet_admin` so
// existing users keep full access. A `maintenanceRole` localStorage key (or an
// explicit override) lets the rest of the epic pin a persona without a backend
// change.
// ---------------------------------------------------------------------------

export type MaintenanceRole =
  | "fleet_admin"
  | "workshop_internal"
  | "workshop_external"
  | "service_buyer";

export interface MaintenanceCapabilities {
  /** Create / edit / delete maintenance records and work orders. */
  canManageRecords: boolean;
  /** Create / edit / delete the priced service catalog. */
  canManageCatalog: boolean;
  /** Assign scheduled dates (agenda). */
  canSchedule: boolean;
  /** Charge an external-workshop order through the POS. */
  canCharge: boolean;
  /** Generate maintenance PDF reports. */
  canViewReports: boolean;
}

export const MAINTENANCE_ROLES: MaintenanceRole[] = [
  "fleet_admin",
  "workshop_internal",
  "workshop_external",
  "service_buyer",
];

// i18n key for each role's human label (common namespace).
export const ROLE_LABEL_KEY: Record<MaintenanceRole, string> = {
  fleet_admin: "mantenimiento.roles.fleet_admin",
  workshop_internal: "mantenimiento.roles.workshop_internal",
  workshop_external: "mantenimiento.roles.workshop_external",
  service_buyer: "mantenimiento.roles.service_buyer",
};

// Capability matrix — the single source of truth for what each persona can do.
const CAPABILITIES: Record<MaintenanceRole, MaintenanceCapabilities> = {
  // Administrador de flota: everything.
  fleet_admin: {
    canManageRecords: true,
    canManageCatalog: true,
    canSchedule: true,
    canCharge: true,
    canViewReports: true,
  },
  // Taller propio: operates work orders and the agenda, no external POS charge.
  workshop_internal: {
    canManageRecords: true,
    canManageCatalog: false,
    canSchedule: true,
    canCharge: false,
    canViewReports: true,
  },
  // Taller externo: operates its orders, owns its catalog, charges via POS.
  workshop_external: {
    canManageRecords: true,
    canManageCatalog: true,
    canSchedule: true,
    canCharge: true,
    canViewReports: true,
  },
  // Comprador del servicio: read-only — consults status and reports only.
  service_buyer: {
    canManageRecords: false,
    canManageCatalog: false,
    canSchedule: false,
    canCharge: false,
    canViewReports: true,
  },
};

// Maps a system `userRole` to a maintenance persona. Unknown/empty roles get
// full access so the mini-app keeps working before dedicated roles exist.
const SYSTEM_ROLE_MAP: Record<string, MaintenanceRole> = {
  monitor: "service_buyer",
};

/** Resolves the active maintenance role from localStorage. */
export function getMaintenanceRole(): MaintenanceRole {
  const explicit = localStorage.getItem("maintenanceRole");
  if (explicit && MAINTENANCE_ROLES.includes(explicit as MaintenanceRole)) {
    return explicit as MaintenanceRole;
  }
  const systemRole = localStorage.getItem("userRole") ?? "";
  return SYSTEM_ROLE_MAP[systemRole] ?? "fleet_admin";
}

/** Capabilities for a given role (defaults to the active role). */
export function getMaintenanceCapabilities(
  role: MaintenanceRole = getMaintenanceRole()
): MaintenanceCapabilities {
  return CAPABILITIES[role];
}
